import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { DropdownCoordinatorService } from '../../../services/dropdown-coordinator.service';

export type DateRange =
  | 'all'
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year';

export const DEFAULT_DATE_RANGE: DateRange = 'all';

interface DateRangeChoice {
  label: string;
  value: DateRange;
}

@Component({
  selector: 'app-reports-date-range',
  standalone: true,
  imports: [NgForOf, NgIf, NgClass],
  templateUrl: './reports-date-range.component.html',
  styleUrl: './reports-date-range.component.scss'
})
export class ReportsDateRangeComponent implements OnInit, OnDestroy {

  @Input() value: DateRange = DEFAULT_DATE_RANGE;
  @Output() dateRangeChanged = new EventEmitter<DateRange>();

  open = false;

  private readonly id = Symbol('reports-date-range');
  private coordinatorSub?: Subscription;

  readonly choices: DateRangeChoice[] = [
    { label: 'All',          value: 'all'          },
    { label: 'Today',        value: 'today'        },
    { label: 'Yesterday',    value: 'yesterday'    },
    { label: 'This Week',    value: 'this_week'    },
    { label: 'Last Week',    value: 'last_week'    },
    { label: 'This Month',   value: 'this_month'   },
    { label: 'Last Month',   value: 'last_month'   },
    { label: 'This Quarter', value: 'this_quarter' },
    { label: 'Last Quarter', value: 'last_quarter' },
    { label: 'This Year',    value: 'this_year'    },
    { label: 'Last Year',    value: 'last_year'    },
  ];

  constructor(
    private host: ElementRef<HTMLElement>,
    private coordinator: DropdownCoordinatorService
  ) {}

  ngOnInit(): void {
    this.coordinatorSub = this.coordinator.current$.subscribe(activeId => {
      if (activeId !== this.id && this.open) {
        this.open = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.coordinatorSub?.unsubscribe();
    this.coordinator.close(this.id);
  }

  get currentLabel(): string {
    const match = this.choices.find(c => c.value === this.value);
    return match ? match.label : this.choices[0].label;
  }

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    if (this.open) {
      this.open = false;
      this.coordinator.close(this.id);
    } else {
      this.open = true;
      this.coordinator.open(this.id);
    }
  }

  select(choice: DateRangeChoice): void {
    this.open = false;
    this.coordinator.close(this.id);
    if (choice.value === this.value) {
      return;
    }
    this.value = choice.value;
    this.dateRangeChanged.emit(this.value);
  }

  isActive(choice: DateRangeChoice): boolean {
    return choice.value === this.value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) {
      return;
    }
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
      this.coordinator.close(this.id);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.open = false;
      this.coordinator.close(this.id);
    }
  }
}
